import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import parse, { DOMNode } from 'html-react-parser';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

import { translate as t } from 'hooks/useTranslations';
import { useTimeout } from 'hooks/useTimeout';
import { AppContext } from 'views/App/context';
import manualHelper from '../../helpers/draftManualHelper';
import useDraftPreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPreface';
import useManagePreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePreface';
import { DraftManualDetailContext } from '../context';
import { ContentPage, TitleTextField } from '../styles';
import errorHelper from 'helpers/errorHelper';
import { DraftPreface } from 'views/App/Portal/Policy/_types/manuals/draft';
import { checkForBlankHeaders } from 'views/App/Portal/Policy/_helpers/policyHeaderHelper';

import ContentEditor from 'components/ContentEditor';
import { ServerErrorResponse } from 'data_sources/constants';
import draftManualHelper from '../../helpers/draftManualHelper';
import PrefaceCompare from './PrefaceCompare';
import { ActionMenu } from 'components/_atoms';
import ToC from '../ToC';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import ContentConversionHelper from '../../helpers/contentConversionHelper';
import { useTrackChanges } from 'components/ContentEditor/context/SharedTrackChangesContext';
import AttachmentBox from '../AttachmentLibrary/_components/AttachmentBox';
import { usePrefaceEditorContext } from './context';
import PendingUpdatesAlert from 'views/App/Portal/Policy/DraftManuals/Detail/PendingUpdatesAlert';
import { useNavigate } from 'react-router-dom';
import contentConversionHelper from '../../helpers/contentConversionHelper';
import { getContentForDisplay } from '../../helpers/contentDisplayHelper';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import ManualActions from '../ManualActions';
import PrefaceTags from './PrefaceTags';
import { Description as DescriptionIcon, BallotOutlined as ReleaseNotesIcon } from '@mui/icons-material';
import GuideSheetEditModal from '../GuideSheet/GuideSheetEditModal';
import { DrawerPanel, ModalAction } from '../PolicyEditor';
import { Header } from '../_components/rightDrawer/Headers';
import { Content } from '../_components/rightDrawer/Contents';
import useReleaseNotes from '../ReleaseNotes/useReleaseNotes';
import ReleaseNotesModal from '../ReleaseNotes/ReleaseNotesEditModal';
import PolicyHeaders from '../_components/PolicyHeaders';

type PrefaceEditorProps = {
  id: string;
};

/***********
 * VIEW - Not locked, no edit permission
 * DISABLED - preface is currently disabled
 * EDITABLE - Not locked, has edit permission
 * EDITING - locked by current user
 * LOCKED - locked by another user
 ******/
export type PrefaceMode = 'VIEW' | 'DISABLED' | 'EDITABLE' | 'EDITING' | 'LOCKED';

const AUTOSAVE_INTERVAL = 60000;

const PrefaceEditor: React.FC<PrefaceEditorProps> = ({ id }: PrefaceEditorProps) => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentProfile, isFeatureEnabled } = useContext(AppContext);
  const { manual, manualHasPermissions, pendingUpdates, showAccreditationTags, setShowAccreditationTags, textEntities } =
    useContext(DraftManualDetailContext);

  const prefaceQuery = useDraftPreface(manual.id, id);
  const {
    approveChanges,
    getLock,
    removeLock,
    takeLock,
    update: updatePreface,
    updateDraftStatus,
    updatetGuideSheet,
    approveGuideSheet,
  } = useManagePreface();
  const timerRef = useRef<NodeJS.Timeout | null>(null); // Initialize timerRef
  const { setTrackChangesState, showChanges } = useTrackChanges();
  const [prefaceContent, setPrefaceContent] = useState<string>('');
  const [prefaceContentEditable, setPrefaceContentEditable] = useState<string>('');
  const [prefaceTitle, setPrefaceTitle] = useState<string>('');
  const [isOpenComparePreface, setIsOpenComparePreface] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [gettingLock, setGettingLock] = useState<boolean>(false);
  const [hasLock, setHasLock] = useState<string | null>(null);
  const [prefaceMode, setPrefaceMode] = useState<PrefaceMode>('VIEW');
  const committedPrefaceQuery = useDraftPreface(manual.id, id, false);
  const [isUpdatingDraftState, setIsUpdatingDraftState] = useState(false);
  const {
    prefaceData,
    removeAttachmentFromPreface,
    acceptNewPreface,
    rejectNewPreface,
    isAcceptingNew,
    isRejectingNew,
  } = usePrefaceEditorContext();
  const {
    currentReleaseNoteIndex,
    currentNote,
    handleNoteIndexChange,
    handleUpdateReleaseNotes,
    handleApproveReleaseNotes,
    amountOfReleaseNotes,
    notesStatus,
  } = useReleaseNotes({ data: prefaceData, invalidateQuery: 'draft_preface', type: 'PREFACE' });
  const navigate = useNavigate();
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [activeDrawerPanel, setActiveDrawerPanel] = useState<DrawerPanel>(null);
  const [activeModalPane, setActiveModalPane] = useState<DrawerPanel>(null);
  const [modalAction, setModalAction] = useState<ModalAction>(null);

  useEffect(() => {
    // setGuideSheetData(prefaceQuery.data?.guideSheet); // Removed as per edit hint
  }, [prefaceQuery.data?.guideSheet]);

  const handleDrawerIconClick = (panel: DrawerPanel) => {
    setActiveDrawerPanel(panel);
    setRightDrawerOpen(true);
  };

  const handleSetRightDrawerOpen = (open: boolean) => {
    setRightDrawerOpen(open);
    if (!open) setActiveDrawerPanel(null);
  };

  const handleOpenModals = (panel: DrawerPanel, action?: ModalAction) => {
    setActiveModalPane(panel);
    setModalAction(action ?? null);
  };

  const handleCloseModals = () => {
    setActiveModalPane(null);
    setModalAction(null);
  };

  useEffect(() => {
    let mode: PrefaceMode = 'VIEW';
    if (manualHasPermissions(manual.id, ['updateDraftPreface']) && prefaceQuery.data) {
      if (prefaceQuery.data.lock) {
        if (prefaceQuery.data.lock.userId === currentProfile?.user?.id) {
          mode = 'EDITING';
          setHasLock(prefaceQuery.data.id);
        } else {
          mode = 'LOCKED';
          if (hasLock === prefaceQuery.data.id) {
            enqueueSnackbar(
              <div
                dangerouslySetInnerHTML={{
                  __html: t('draftManuals.preface.lostLock', '', {
                    user: draftManualHelper.formatPrefaceLockInfo(prefaceQuery.data),
                  }),
                }}
              />,
              {
                variant: 'error',
                autoHideDuration: 10000,
              },
            );
            setHasLock(null);
          }
        }
      } else {
        mode = 'EDITABLE';
      }
    }
    if (prefaceQuery?.data?.state === 'DISABLED') {
      mode = 'DISABLED';
    }
    if (prefaceMode === 'EDITING' && mode === 'DISABLED') {
      enqueueSnackbar(
        <div
          dangerouslySetInnerHTML={{
            __html: t('draftManuals.preface.savedButDisabled'),
          }}
        />,
        {
          variant: 'error',
          autoHideDuration: 10000,
        },
      );
    }

    if (mode !== prefaceMode) setPrefaceMode(mode);
  }, [manualHasPermissions, manual.id, prefaceQuery.data, currentProfile?.user?.id]);

  const handleModifyClick = async () => {
    setGettingLock(true);
    await getLock(
      manual.id,
      id,
      (lock) => {
        queryClient.invalidateQueries(['draft_manual', manual.id]);
        queryClient.invalidateQueries(['draft_preface', manual.id]);
        setGettingLock(false);
      },
      (error: ServerErrorResponse) => {
        errorHelper.enqueueErrors(error);
        setGettingLock(false);
      },
    );
  };

  const handleAcquireLockClick = async () => {
    setGettingLock(true);
    await confirm({
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('draftManuals.preface.acquireLock.ok'),
      description: t('draftManuals.preface.acquireLock.body'),
      title: t('draftManuals.preface.acquireLock.title'),
    }).then(
      async () =>
        await new Promise<void>((resolve, reject) => {
          takeLock(
            manual.id,
            id,
            (lock) => {
              localStorage.setItem(`preface-lock_${id}`, 'true');
              queryClient.invalidateQueries(['draft_manuals']);
              queryClient.invalidateQueries(['draft_manual', manual.id]);
              queryClient.invalidateQueries(['draft_preface', manual.id, id]);
              setGettingLock(false);
              enqueueSnackbar(<Box>{parse(t('draftManuals.preface.acquireLock.success'))}</Box>, {
                variant: 'success',
                autoHideDuration: 5000,
              });
              resolve();
            },
            (error: ServerErrorResponse) => {
              errorHelper.enqueueErrors(error);
              setGettingLock(false);
              reject(error);
            },
          );
        }).catch(() => {
          // don't do it
          setGettingLock(false);
        }),
    );
  };

  useEffect(() => {
    if (prefaceQuery.data) {
      setPrefaceContent(prefaceQuery.data.content ?? '');
      setPrefaceContentEditable(prefaceQuery.data.contentEditable ?? '');
      setPrefaceTitle(prefaceQuery.data.title ?? '');
      if (localStorage.getItem(`preface-lock_${id}`) !== null) {
        setIsOpenComparePreface(true);
      }
    }
  }, [prefaceQuery.data?.title, prefaceQuery.data?.content, prefaceQuery.data?.contentEditable]);

  // NOTE: This function previously converted editor-insert to editor-insert-disabled.
  // With the new container-based approach, we keep the original classes and let CSS
  // handle visibility based on container classes (e.g., .phx-preface.show-changes).
  // This function is kept for backwards compatibility but no longer modifies classes.
  const purifyPrefaceContent = (content: string): string => {
    // No longer converting classes - tracked changes visibility is controlled via container classes
    return content;
  };

  const handleSave = useCallback(() => {
    setIsSaving(true);
    setTrackChangesState(false);

    // Extract attachment IDs from content
    const parser = new DOMParser();
    const doc = parser.parseFromString(prefaceContentEditable, 'text/html');
    const imgElements = doc.querySelectorAll('img[data-attachment-id]');
    const attachmentIds = Array.from(imgElements)
      .map((img) => img.getAttribute('data-attachment-id'))
      .filter((id): id is string => id !== null);

    // Filter out embedded attachments that don't have corresponding images
    const existingAttachments = (prefaceQuery.data?.attachments || []).filter((att) => {
      if (att.embedded) {
        return attachmentIds.includes(att.id);
      }
      return true;
    });

    // Process non-embedded attachments and append them to content
    let processedContent = prefaceContent;
    let processedContentEditable = prefaceContentEditable;

    const nonEmbeddedAttachments = existingAttachments.filter((att) => !att.embedded);
    if (nonEmbeddedAttachments.length > 0) {
      const attachmentsHtml = nonEmbeddedAttachments
        .map((att) => {
          const manualAttachment = manual.attachments.find((a) => a.id === att.id);
          if (manualAttachment) {
            return `<p><a href="" data-attachment-id="${att.id}" data-manual-id="${manual.id}" title="${manualAttachment.name}">${manualAttachment.name}</a></p>`;
          }
          return '';
        })
        .join('');

      processedContent += attachmentsHtml;
      processedContentEditable += attachmentsHtml;
    }

    const purifiedContent = purifyPrefaceContent(processedContent);
    const purifiedContentEditable = purifyPrefaceContent(processedContentEditable);
    const updatedPreface: DraftPreface = {
      ...prefaceData!,
      title: prefaceTitle,
      content: purifiedContent,
      contentEditable: purifiedContentEditable,
      attachments: [
        ...existingAttachments,
        ...attachmentIds
          .filter((id) => !existingAttachments.some((att) => att.id === id))
          .map((id) => ({ id, embedded: true })),
      ],
    };
    updatePreface(
      manual.id,
      id,
      updatedPreface,
      () => {
        removeLock(
          manual.id,
          id,
          () => {
            queryClient.invalidateQueries('draft_manuals');
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_preface', manual.id, id]);
            setIsSaving(false);
            setHasLock(null);
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
            setIsSaving(false);
          },
        );
      },
      (errors) => {
        if (errors.errorInformationCode === 3134) {
          queryClient.invalidateQueries('draft_manuals');
          queryClient.invalidateQueries(['draft_manual', manual.id]);
          queryClient.invalidateQueries(['draft_preface', manual.id, id]);
        } else {
          errorHelper.enqueueErrors(errors);
        }
        setIsSaving(false);
      },
    );
  }, [prefaceQuery.data, prefaceTitle, prefaceContent, prefaceContentEditable, manual.id, manual.attachments]);

  const OnSaveContent = (contentEditable: string) => {
    const content = ContentConversionHelper.contentForRender(contentEditable);
    setPrefaceContentEditable(ContentConversionHelper.contentForEditor(contentEditable));
    setPrefaceContent(content);
  };

  const handleUpdateDraftStatus = useCallback(
    async (newStatus: string, callback?: () => void) => {
      if (newStatus === 'APPROVED') {
        const doApprove = async () => {
          await approveChanges(
            manual.id,
            id,
            () => {
              queryClient.invalidateQueries('draft_manuals');
              queryClient.invalidateQueries(['draft_manual', manual.id]);
              queryClient.invalidateQueries(['draft_preface', manual.id, id]);
              if (callback) callback();
            },
            (error: ServerErrorResponse) => {
              errorHelper.enqueueErrors(error);
            },
          );
        };

        if (checkForBlankHeaders(prefaceData?.headers, manual.headerConfiguration, manual.manualLevel)) {
          try {
            await confirm({
              title: t('draftManuals.manualProperties.policyHeaders.blankHeadersWarning.title'),
              description: t('draftManuals.manualProperties.policyHeaders.blankHeadersWarning.description'),
              confirmationText: t('common.yes'),
              cancellationText: t('draftManuals.manualProperties.policyHeaders.blankHeadersWarning.cancel'),
              confirmationButtonProps: { variant: 'contained', color: 'primary' },
              cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
            });
            await doApprove();
          } catch {
            callback?.();
          }
        } else {
          await doApprove();
        }
      } else {
        await updateDraftStatus(
          manual.id,
          id,
          newStatus,
          () => {
            queryClient.invalidateQueries('draft_manuals');
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_preface', manual.id, id]);
            if (callback) callback();
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
          },
        );
      }
    },
    [manual, id, updateDraftStatus, approveChanges, queryClient, confirm, prefaceData],
  );

  const autoSave = () => {
    if (prefaceMode === 'EDITING') {
      const updatedPreface: DraftPreface = {
        ...prefaceData!,
        title: prefaceTitle,
        content: prefaceContent,
        contentEditable: prefaceContentEditable,
      };
      updatePreface(
        manual.id,
        id,
        updatedPreface,
        () => {
          // don't refresh queries
        },
        (errors) => {
          if (errors.errorInformationCode === 3134) {
            queryClient.invalidateQueries('draft_manuals');
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_preface', manual.id, id]);
          } else {
            errorHelper.enqueueErrors(errors);
          }
          setIsSaving(false);
        },
      );
    }
  };

  const revertChanges = async () => {
    if (prefaceMode === 'EDITING') {
      const updatedPreface: DraftPreface = {
        ...committedPrefaceQuery.data!,
      };
      await updatePreface(
        manual.id,
        id,
        updatedPreface,
        (data) => {
          if (data) {
            setPrefaceContent(data.content);
            setPrefaceContentEditable(data.contentEditable);
            setPrefaceTitle(data.title);
          }
          queryClient.invalidateQueries(['draft_manual', manual.id]);
          queryClient.invalidateQueries(['draft_preface', manual.id, id]);
        },
        (errors) => {
          if (errors.errorInformationCode === 3134) {
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_preface', manual.id, id]);
          } else {
            errorHelper.enqueueErrors(errors);
          }
          setIsSaving(false);
        },
      );
    }
  };

  const onHandleModify = async () => {
    try {
      await handleAcquireLockClick();
    } catch (err) {
      setGettingLock(false);
    }
  };

  useTimeout(() => {
    autoSave();
  }, AUTOSAVE_INTERVAL);

  useEffect(() => {
    const autoSaveListener = () => {
      autoSave();
    };
    document.body.addEventListener('auto-save-preface', autoSaveListener);
    return () => {
      document.body.removeEventListener('auto-save-preface', autoSaveListener);
    };
  }, [autoSave]);

  const draftStateOptions = [
    'DRAFT',
    'IN_APPROVAL',
    ...(prefaceQuery.data?.draftState === 'IN_APPROVAL' ? ['APPROVED'] : []),
  ];
  if (!draftStateOptions.includes(prefaceQuery.data?.draftState ?? 'UNKNOWN')) {
    draftStateOptions.push(prefaceQuery.data?.draftState ?? 'UNKNOWN');
  }

  const prefaceIssuedAt = prefaceQuery.data?.issuedAt;
  const isNeverIssued = prefaceIssuedAt == null || (typeof prefaceIssuedAt === 'string' && !prefaceIssuedAt.trim());
  const isDraftStateChangeAllowed = prefaceQuery.data?.hasPublishableChanges === true || isNeverIssued;
  const selectDisabled = isUpdatingDraftState || !isDraftStateChangeAllowed;

  const renderPrefaceActions = useCallback(() => {
    let actions: React.ReactNode = null;

    if (prefaceQuery.data && prefaceMode === 'EDITABLE') {
      actions = (
        <Stack
          direction='row'
          spacing={3}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isUpdatingDraftState ? (
            <CircularProgress />
          ) : (
            <Tooltip
              title={t('draftManuals.draftState.noPublishableChanges')}
              disableHoverListener={isDraftStateChangeAllowed}
              placement='bottom'
            >
              <span>
                <Select
                  id='draft_state'
                  label={false}
                  value={prefaceQuery.data?.draftState ?? ''}
                  size='small'
                  disabled={selectDisabled}
                  onChange={async ({ target }) => {
                    setIsUpdatingDraftState(true);
                    await handleUpdateDraftStatus(target.value, () => setIsUpdatingDraftState(false));
                  }}
                >
                  {draftStateOptions.map((draftState: string) => (
                    <MenuItem
                      key={`draftState_${draftState}`}
                      value={draftState}
                    >
                      {[t('draftManuals.draftState.label'), t(`draftManuals.draftState.${draftState}`)].join(': ')}
                    </MenuItem>
                  ))}
                </Select>
              </span>
            </Tooltip>
          )}
          <Button
            disabled={gettingLock}
            onClick={handleModifyClick}
          >
            {t('draftManuals.actions.editPreface')}
          </Button>
        </Stack>
      );
    }
    if (prefaceQuery.data && prefaceMode === 'EDITING') {
      actions = (
        <Stack
          direction='row'
          spacing={3}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {!!prefaceQuery.data?.draftState && (
            <Chip
              label={t(`draftManuals.draftState.${prefaceQuery.data.draftState}`)}
              variant='outlined'
              color='neutral'
              sx={{
                height: 36,
                px: 2,
                '& .MuiChip-label': {
                  fontSize: 16,
                },
              }}
            />
          )}
          <ActionMenu
            id='preface_actions'
            btnText={t('common.actions')}
            items={[
              {
                key: 'comparePreface',
                label: t('draftManuals.preface.comparePreface'),
                onClick: () => setIsOpenComparePreface(true),
              },
              {
                key: 'reviewUpdates',
                label: t('draftManuals.reviewUpdates.title'),
                onClick: () => navigate(`./preface:${prefaceData?.id}/review-updates`),
              },
            ]}
            btnProps={{
              size: 'small',
              variant: 'outlined',
              color: 'neutral',
            }}
          />
          <Button
            onClick={handleSave}
            disabled={isSaving}
          >
            {t('draftManuals.actions.savePreface')}
          </Button>
        </Stack>
      );
    }
    if (prefaceQuery.data && prefaceMode === 'LOCKED') {
      actions = (
        <Stack
          direction='row'
          spacing={4}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {!!prefaceQuery.data?.draftState && (
            <Chip
              label={t(`draftManuals.draftState.${prefaceQuery.data.draftState}`)}
              variant='outlined'
              color='neutral'
              sx={{
                height: 36,
                px: 2,
                '& .MuiChip-label': {
                  fontSize: 16,
                },
              }}
            />
          )}
          {prefaceQuery.data?.lock && <>{draftManualHelper.prefaceLockAvatar(prefaceQuery.data)}</>}
          <Button
            onClick={onHandleModify}
            disabled={gettingLock}
            startIcon={<LockIcon />}
          >
            {t('common.modify')}
          </Button>
        </Stack>
      );
    }
    if (prefaceMode === 'DISABLED') {
      actions = (
        <Badge
          variant='inline'
          badgeContent={t('common.disabled')}
          color='warning'
          sx={{ textTransform: 'uppercase' }}
        />
      );
    }

    return actions;
  }, [prefaceQuery.data, prefaceMode, prefaceContent, prefaceTitle]);

  const handlePendingUpdates = async () => {
    if (prefaceQuery.data && prefaceMode === 'EDITABLE') await handleModifyClick();
    if (prefaceQuery.data && prefaceMode === 'LOCKED') {
      try {
        await handleAcquireLockClick();
        navigate(`./preface:${prefaceData?.id}/review-updates`);
      } catch (err) {
        setGettingLock(false);
      }
    } else navigate(`./preface:${prefaceData?.id}/review-updates`);
  };

  const handleApproveGuideSheet = async () => {
    if (manual && prefaceQuery.data) {
      return new Promise<void>((resolve, reject) => {
        approveGuideSheet(
          manual.id,
          prefaceQuery.data.id,
          () => {
            queryClient.invalidateQueries(['draft_preface', manual.id, prefaceQuery.data.id]);
            resolve();
          },
          (err) => {
            reject(err);
          },
        );
      });
    }
  };

  return (
    <>
      <TakeoverLayout
        pageTitle={manual ? parse(manual.name ?? '') : ''}
        backUrl='/admin/policy/draft-manuals'
        leftDrawer={<ToC key={`preface:${id}`} />}
        menubarLeft={
          <>
            {manual && (
              <ManualActions
                manual={manual}
                manualHasPermissions={manualHasPermissions}
                currentUserId={currentProfile?.user?.id}
                showContentFilters={!!prefaceQuery.data}
                prefaceMode={prefaceMode}
                showAccreditationTags={showAccreditationTags}
                setShowAccreditationTags={setShowAccreditationTags}
              />
            )}
          </>
        }
        contentHeaderRight={
          <>
            <Stack
              direction='row'
              spacing={4}
              sx={{ flexGrow: 1 }}
            >
              {' '}
            </Stack>
            {(gettingLock || isSaving || isAcceptingNew || isRejectingNew) && <CircularProgress />}
            <Stack
              direction='row'
              spacing={4}
              sx={{ ml: 2 }}
            >
              {renderPrefaceActions()}
            </Stack>
          </>
        }
        rightDrawer={{
          verticalActions: [
            {
              key: 'guideSheet',
              icon: <DescriptionIcon />,
              label: t('draftManuals.guideSheet.title'),
              onClick: () => handleDrawerIconClick('guideSheet'),
              active: activeDrawerPanel === 'guideSheet',
            },
            {
              key: 'releaseNotes',
              icon: <ReleaseNotesIcon />,
              label: t('draftManuals.releaseNotes.title'),
              onClick: () => handleDrawerIconClick('releaseNotes'),
              active: activeDrawerPanel === 'releaseNotes',
            },
          ],
          header: (
            <Header
              activePaneId={activeDrawerPanel ?? ''}
              handleOpenModals={handleOpenModals}
              releaseNotes={{ amountOfReleaseNotes, showControls: prefaceMode === 'EDITING' }}
              guideSheet={{ showControls: prefaceMode === 'EDITING' }}
            />
          ),
          content: (
            <Content
              activePaneId={activeDrawerPanel ?? ''}
              mode={prefaceMode}
              isLoading={prefaceQuery.isFetching || prefaceQuery.isLoading}
              guideSheets={{
                guideSheet: prefaceQuery.data?.guideSheet,
                onApproveGuideSheet: handleApproveGuideSheet,
              }}
              releaseNotes={{
                onNoteIndexChange: handleNoteIndexChange,
                currentNoteIndex: currentReleaseNoteIndex,
                handleApproveReleaseNotes,
                releaseNotes: prefaceQuery.data?.releaseNotes,
              }}
            />
          ),
          wide: false,
        }}
        rightDrawerOpen={rightDrawerOpen}
        setRightDrawerOpen={handleSetRightDrawerOpen}
      >
        <Stack
          spacing={4}
          sx={{ mx: 'auto', maxWidth: 900, width: '100%', px: 2 }}
        >
          {prefaceQuery.data?.isNew && prefaceQuery.data?.state === 'DISABLED' && (
            <PendingUpdatesAlert
              info={t('draftManuals.preface.newPreface.prompt')}
              showAcceptReject={true}
              onAccept={acceptNewPreface}
              onReject={rejectNewPreface}
              containerClass={{ mx: 8, mb: 4 }}
              isLoading={isAcceptingNew || isRejectingNew}
            />
          )}
          {pendingUpdates?.prefaces[id] !== null && pendingUpdates?.prefaces[id]?.count && prefaceMode !== 'EDITING' && (
            <PendingUpdatesAlert
              info={t('draftManuals.pendingUpdates.alert', '', {
                count: pendingUpdates?.prefaces[id].count.toString(),
              })}
              actionText={t('draftManuals.pendingUpdates.actionText')}
              onClick={handlePendingUpdates}
              containerClass={{ mx: 8, mb: 4 }}
              isLoading={gettingLock}
              disable={prefaceMode === 'DISABLED'}
            />
          )}

          {isFeatureEnabled('policy-headers')
            && manual?.headerConfiguration
            && manual.headerConfiguration.state === 'ENABLED' && (
              <PolicyHeaders
                manualHeaders={manual.headerConfiguration}
                policyHeaders={prefaceData?.headers}
                manualId={manual.id}
                policyData={prefaceData as any}
                readOnly={prefaceMode !== 'EDITING'}
                isPreface={true}
                isLoading={prefaceQuery.isLoading || prefaceQuery.isFetching}
              />
            )}

          <ContentPage className={`phx-preface ${showChanges ? 'show-changes' : 'hide-changes'}`}>
            <Stack spacing={4}>
              {prefaceQuery.data ? (
                <>
                  {prefaceMode === 'EDITING' ? (
                    <Box>
                      <Stack spacing={3}>
                        <Typography variant='overline'>
                          {t('draftManuals.prefaceEditor.title')}
                        </Typography>
                        <TitleTextField
                          id={`title_${prefaceQuery.data.id}`}
                          key={`title_${prefaceQuery.data.id}`}
                          label={false}
                          variant='outlined'
                          fullWidth
                          value={prefaceTitle}
                          onChange={(e) => setPrefaceTitle(e.target.value)}
                        />
                        <PrefaceTags prefaceId={prefaceQuery.data.id} />
                        <ContentEditor
                          id={`content_${prefaceQuery.data.id}`}
                          key={`content_${prefaceQuery.data.id}`}
                          contentHTML={prefaceContentEditable}
                          onSave={(content) => OnSaveContent(content)}
                          isEditable
                          trackChangesEnabled={!isFeatureEnabled('disable-track-changes-plugin')}
                          textEntitiesEnabled={true}
                          textEntities={textEntities}
                          crossReferencesEnabled={manual.manualLevel === 'AGENCY'}
                          manualId={manual.id}
                          crossReferences={(prefaceQuery.data as any)?.crossReferences}
                        />
                        {prefaceQuery.data?.attachments &&
                          prefaceQuery.data.attachments.filter((att: any) => !att.embedded).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant='subtitle2'>{t('draftManuals.preface.attachmentsLabel')}</Typography>
                              <Stack
                                direction='row'
                                spacing={1}
                              >
                                {prefaceQuery.data?.attachments
                                  .filter((att: any) => !att.embedded)
                                  .map((att: any) => (
                                    <AttachmentBox
                                      key={att.id}
                                      attachmentRef={att}
                                      manualId={manual.id}
                                      onRemove={(aid) => removeAttachmentFromPreface(aid)}
                                      isEditable={true}
                                    />
                                  ))}
                              </Stack>
                            </Box>
                          )}
                      </Stack>
                    </Box>
                  ) : (
                    <Box>
                      <Stack spacing={1}>
                        <Typography variant='overline'>
                          {t('draftManuals.prefaceEditor.title')}
                        </Typography>
                        <Typography
                          component='div'
                          variant='h5'
                        >
                          {manualHelper.formatManualNodeTitle(prefaceQuery.data, manual)}
                        </Typography>
                        <PrefaceTags prefaceId={prefaceQuery.data.id} />
                        <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                          {parse(getContentForDisplay(prefaceQuery.data?.content ?? '', textEntities), {
                            replace(node: DOMNode, ind: number) {
                              return PolicyContentHelper.handleContent(node, ind);
                            },
                          })}
                        </Box>
                        {prefaceQuery.data?.attachments &&
                          prefaceQuery.data.attachments.filter((att: any) => !att.embedded).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant='subtitle2'>{t('draftManuals.preface.attachmentsLabel')}</Typography>
                              <Stack
                                direction='row'
                                spacing={1}
                              >
                                {prefaceQuery.data?.attachments
                                  .filter((att: any) => !att.embedded)
                                  .map((att: any) => (
                                    <AttachmentBox
                                      key={att.id}
                                      attachmentRef={att}
                                      manualId={manual.id}
                                      onRemove={(aid) => removeAttachmentFromPreface(aid)}
                                      isEditable={true}
                                    />
                                  ))}
                              </Stack>
                            </Box>
                          )}
                      </Stack>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ minHeight: 0 }}>
                  {prefaceQuery?.isError && errorHelper.renderErrors(prefaceQuery)}
                  {prefaceQuery && (prefaceQuery.isLoading || prefaceQuery.isFetching) && (
                    <Stack spacing={2}>
                      <Skeleton
                        variant='rectangular'
                        height={28}
                        width='50%'
                      />
                      <Stack>
                        <Skeleton variant='text' />
                        <Skeleton variant='text' />
                        <Skeleton variant='text' />
                      </Stack>
                    </Stack>
                  )}
                </Box>
              )}
            </Stack>
          </ContentPage>
        </Stack>
      </TakeoverLayout>
      <PrefaceCompare
        id={id}
        committedPrefaceQuery={committedPrefaceQuery}
        currentPreface={
          {
            ...prefaceQuery.data,
            title: prefaceTitle,
            content: prefaceContentEditable,
          } as DraftPreface
        }
        isOpenComparePreface={isOpenComparePreface}
        revertChanges={revertChanges}
        setIsOpenComparePreface={setIsOpenComparePreface}
      />
      <ReleaseNotesModal
        key={`${currentNote?.updatedAt || 'new'}-${activeModalPane === 'releaseNotes' ? 'open' : 'closed'}`}
        open={activeModalPane === 'releaseNotes'}
        onClose={handleCloseModals}
        initialData={modalAction === 'EDIT' ? currentNote : undefined}
        status={notesStatus}
        onSave={handleUpdateReleaseNotes}
        manualLevel={manual?.manualLevel}
      />
      <GuideSheetEditModal
        open={activeModalPane === 'guideSheet'}
        onClose={handleCloseModals}
        initialTitle={prefaceQuery.data?.guideSheet?.title || ''}
        initialStatus={(prefaceQuery.data?.guideSheet?.draftState || 'DRAFT') as 'DRAFT' | 'APPROVED'}
        initialContent={prefaceQuery.data?.guideSheet?.contentEditable || ''}
        onSave={async ({ title, status, content }) => {
          if (manual && prefaceQuery.data) {
            return new Promise<void>((resolve, reject) => {
              updatetGuideSheet(
                manual.id,
                prefaceQuery.data.id,
                {
                  title,
                  contentEditable: ContentConversionHelper.contentForEditor(content),
                  content: ContentConversionHelper.contentForRender(content),
                },
                () => {
                  queryClient.invalidateQueries(['draft_preface', manual.id, prefaceQuery.data.id]);
                  resolve();
                },
                (error) => {
                  reject(error);
                },
              );
            });
          }
        }}
        editorHeight={400}
        onStatusChange={async (newStatus) => {
          if (newStatus === 'APPROVED' && manual && prefaceQuery.data) {
            await approveGuideSheet(manual.id, prefaceQuery.data.id, () => {
              queryClient.invalidateQueries(['draft_preface', manual.id, prefaceQuery.data.id]);
            });
          }
        }}
      />
    </>
  );
};

export default PrefaceEditor;
